import {
  ActionCodeSettings,
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
  UserCredential,
} from 'firebase/auth';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import client from '../apolloClient';
import { auth } from '../firebase';
import { DtType } from '../generated/graphql';
import { GET_CURRENT_USER } from '../utils/queries';

// Email verification redirect URL - uses current origin for dev/prod compatibility
const getEmailVerificationSettings = (): ActionCodeSettings => ({
  url: `${window.location.origin}/verify-email`,
  handleCodeInApp: true,
});

// Define a proper type for the backend user
interface BackendUser {
  id: string;
  fullName: string;
  email: string;
  firebaseUid?: string;
  tosVerified?: boolean;
  hasOnboarded?: boolean | null;
  currentOrganization?: {
    id: string;
    name: string;
    personaAccessTypes?: DtType[] | null;
  } | null;
  canCreateProject?: boolean;
}

interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  backendUser: BackendUser | null;
  loading: boolean;
  error: string | null;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<UserCredential>;
  logout: () => Promise<void>;
  refreshBackendUser: () => Promise<BackendUser | null>;
  setBackendUserData: (userData: BackendUser | null) => void;
  sendVerificationEmail: () => Promise<boolean>;
  checkEmailVerified: () => Promise<boolean>;
  isEmailVerified: boolean;
  isTosVerified: boolean;
  hasOnboarded?: boolean | null;
  setLoading: (isLoading: boolean) => void; // Added new method to explicitly control loading
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Set up Firebase to use local persistence by default (this is important)
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('Error setting auth persistence:', error);
});

// Utility function to store backend user in localStorage
const storeBackendUser = (user: BackendUser | null) => {
  if (user) {
    localStorage.setItem('backendUser', JSON.stringify(user));
  } else {
    localStorage.removeItem('backendUser');
  }
};

// Utility function to get the stored backend user
const getStoredBackendUser = (): BackendUser | null => {
  const storedUser = localStorage.getItem('backendUser');
  if (storedUser) {
    try {
      return JSON.parse(storedUser);
    } catch (error) {
      console.error('Error parsing stored user:', error);
      return null;
    }
  }
  return null;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [backendLoading, setBackendLoading] = useState(false);
  const [backendUser, setBackendUser] = useState<BackendUser | null>(() =>
    getStoredBackendUser()
  );
  const [error, setError] = useState<string | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Derive TOS verification status from backend user
  const isTosVerified = backendUser?.tosVerified || false;
  const hasOnboarded = backendUser?.hasOnboarded || false;
  const canCreateProject = backendUser?.canCreateProject || false;
  const safeResetApolloStore = async () => {
    try {
      // First stop all operations
      await client.stop();

      // Then clear and reset with proper error handling
      try {
        await client.clearStore();
      } catch (e) {
        console.warn('Error during Apollo clearStore:', e);
        // Continue despite error
      }

      try {
        await client.resetStore();
      } catch (e) {
        console.warn('Error during Apollo resetStore:', e);
        // Continue despite error
      }
    } catch (e) {
      console.error('Apollo store reset failed completely:', e);
    }
  };

  // Function to set backend user and also update localStorage
  const setBackendUserData = useCallback((userData: BackendUser | null) => {
    setBackendUser(userData);
    storeBackendUser(userData);
  }, []);

  // Function to fetch backend user data
  const fetchBackendUser =
    useCallback(async (): Promise<BackendUser | null> => {
      if (!auth.currentUser) {
        setBackendUserData(null);
        return null;
      }
      setError(null);

      try {
        // Get a fresh token to ensure we have the latest authentication
        await auth.currentUser.getIdToken(true);

        // Clear Apollo cache to ensure fresh data
        await safeResetApolloStore();
        const { data } = await client.query({
          query: GET_CURRENT_USER,
          fetchPolicy: 'network-only',
        });

        if (data?.user) {
          setBackendUserData(data.user);
          return data.user;
        }
        return null;
      } catch (error: any) {
        console.error('Error fetching user:', error);
        setError(`Error fetching user: ${error.message}`);
        return null;
      } finally {
        setBackendLoading(false);
      }
    }, [setBackendUserData]);

  // Public method to refresh the backend user
  const refreshBackendUser = useCallback(async () => {
    return fetchBackendUser();
  }, [fetchBackendUser]);

  // Check for backend user when Firebase user changes
  useEffect(() => {
    if (currentUser && initialLoadComplete) {
      // Add a slight delay to ensure Firebase auth is fully completed
      fetchBackendUser();
    }
  }, [currentUser, fetchBackendUser, initialLoadComplete]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          // Get a fresh token on auth state change
          await user.getIdToken(true);

          // Try to get the stored backend user first for immediate UI update
          const storedUser = getStoredBackendUser();
          if (storedUser) {
            setBackendUser(storedUser);
          }

          // Add a significant delay before fetching backend user data
          // This is critical - give the backend user creation time to complete
          await new Promise((resolve) => setTimeout(resolve, 3000));

          // Then fetch from backend to ensure data is fresh
          try {
            const { data } = await client.query({
              query: GET_CURRENT_USER,
              fetchPolicy: 'network-only',
            });

            if (data?.user) {
              setBackendUserData(data.user);
            } else {
              // Only log an error if we didn't have a stored user
              if (!storedUser) {
                setBackendUser(null);
              }
            }
          } catch (fetchError) {
            console.error('Error fetching backend user:', fetchError);
            // Keep using the stored user if available
          }
        } catch (error) {
          console.error('Error restoring session:', error);

          // Keep using the stored user if available
          const storedUser = getStoredBackendUser();
          if (storedUser) {
            setBackendUser(storedUser);
          } else {
            setBackendUser(null);
          }
        }
      } else {
        // User logged out or no user
        setBackendUserData(null);
        await safeResetApolloStore();
      }

      setLoading(false);
      setInitialLoadComplete(true);
    });

    return () => unsubscribe();
  }, [setBackendUserData]);

  // Auth methods
  const signUpWithEmail = async (email: string, password: string) => {
    try {
      setError(null);
      // This function will throw an error if the email is already in use
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Ensure Firebase auth is complete
      await refreshBackendUser();

      // Send verification email immediately after signup
      if (userCredential.user) {
        try {
          await sendEmailVerification(userCredential.user, getEmailVerificationSettings());
          console.log('Verification email sent successfully to:', email);
        } catch (emailError: any) {
          console.error('Failed to send verification email:', emailError);
          // Don't throw - user is still created, they can resend from verification page
        }
      }
    } catch (error: any) {
      console.error('Error signing up:', error);

      // Do not modify the error, let the calling component handle it
      setError(`Signup failed: ${error.message}`);
      throw error; // Re-throw so the calling component can handle it
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);

      // Explicitly fetch backend user after login
      await new Promise((resolve) => setTimeout(resolve, 500)); // Ensure Firebase auth is complete
      await refreshBackendUser();
    } catch (error: any) {
      console.error('Error logging in:', error);
      setError(`Login failed: ${error.message}`);
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);

      // Return the credential so the calling component can use it
      return userCredential;
    } catch (error: any) {
      console.error('Error with Google login:', error);
      setError(`Google login failed: ${error.message}`);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      setBackendUserData(null); // This also clears localStorage
      await safeResetApolloStore();

      // Clear ALL Statsig-related localStorage data
      // This includes cache, stable IDs, and any other Statsig data
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.toLowerCase().includes('statsig') ||
            key.includes('STATSIG') ||
            key.includes('featuregate') ||
            key.includes('dynamicconfig') ||
            key.includes('layer') ||
            key.includes('experiment') ||
            // Also catch any keys that start with common Statsig patterns
            key.startsWith('statsig.') ||
            key.startsWith('STATSIG.'))
        ) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });

      // Also clear sessionStorage just in case
      const sessionKeysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          (key.toLowerCase().includes('statsig') ||
            key.includes('STATSIG') ||
            key.startsWith('statsig.') ||
            key.startsWith('STATSIG.'))
        ) {
          sessionKeysToRemove.push(key);
        }
      }
      if (sessionKeysToRemove.length > 0) {
        sessionKeysToRemove.forEach((key) => {
          sessionStorage.removeItem(key);
        });
      }
    } catch (error: any) {
      console.error('Error logging out:', error);
      setError(`Logout failed: ${error.message}`);
      throw error;
    }
  };

  const sendVerificationEmail = async () => {
    try {
      if (currentUser && !currentUser.emailVerified) {
        await sendEmailVerification(currentUser, getEmailVerificationSettings());
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      setError(`Failed to send verification email: ${error.message}`);
      throw error;
    }
  };

  const checkEmailVerified = async () => {
    if (currentUser) {
      // Reload user to get fresh data
      await currentUser.reload();
      // Get user again after reload
      const user = auth.currentUser;
      const verified = user?.emailVerified || false;
      // Explicitly set loading to false after email verification check
      // This ensures loading state is reset between verification and TOS
      if (verified) {
        setLoading(false);
      }
      return verified;
    }
    return false;
  };

  // Safety mechanism for loading state - ensure it doesn't stay true indefinitely
  useEffect(() => {
    // Only apply timeout if loading is true
    if (loading) {
      // Set a reasonable timeout (3 seconds) for loading state
      const timeoutId = setTimeout(() => {
        console.warn('Loading state timeout reached - auto-resetting');
        setLoading(false);
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [loading]);

  // Update the value object to include the new properties and functions
  const isEmailVerified = currentUser?.emailVerified || false;

  // User is authenticated when both Firebase and backend user exist
  const isAuthenticated = !!(currentUser && backendUser);

  const value = {
    currentUser,
    isAuthenticated,
    backendUser,
    loading: loading || backendLoading,
    error,
    signUpWithEmail,
    loginWithEmail,
    loginWithGoogle,
    logout,
    refreshBackendUser,
    setBackendUserData,
    sendVerificationEmail,
    checkEmailVerified,
    isEmailVerified,
    isTosVerified,
    hasOnboarded,
    setLoading,
    canCreateProject,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
